import { useTranslation } from '@multaqa/i18n'
import { AppError, useAppErrorMessages } from '@multaqa/ui/components/async/app-error'
import { ImportReport } from '@multaqa/ui/components/import/import-report'
import { ImportStatusBadge } from '@multaqa/ui/components/import/import-status-badge'
import { Alert, AlertDescription } from '@multaqa/ui/components/ui/alert'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { Skeleton } from '@multaqa/ui/components/ui/skeleton'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { Spinner } from '@multaqa/ui/components/ui/spinner'
import { XIcon } from 'lucide-react'
import { useState } from 'react'
import { type DataImport, isWorking, useDataImport, useImportAction } from './api'
import { useImportViewText } from './data-import-labels'

type Severity = 'info' | 'success' | 'warning' | 'danger'

const SEVERITY: Record<string, Severity> = {
  validating: 'info',
  applying: 'info',
  ready: 'info',
  invalid: 'danger',
  failed: 'danger',
  stale: 'warning',
  applied: 'success',
  cancelled: 'info',
  expired: 'info',
}

/** A ready import with warnings still applies; its message says to review them first. */
function statusMessage(item: DataImport): string {
  return item.status === 'ready' && (item.report?.warnings.length ?? 0) > 0
    ? 'readyWithWarnings'
    : item.status
}

/** One import: its status, its report, and Apply or Cancel while it is ready. */
export function DataImportPanel({
  groupId,
  companyId,
  companyName,
  importId,
  onClose,
  onAct,
}: {
  groupId: string
  companyId: string
  companyName: string
  importId: string
  onClose: () => void
  /** Keeps the panel open after Apply or Cancel, so the result stays visible. */
  onAct: () => void
}) {
  const { t } = useTranslation()
  const text = useImportViewText()
  const messages = useAppErrorMessages()
  const query = useDataImport(groupId, companyId, importId)
  const apply = useImportAction(groupId, companyId, 'apply')
  const cancel = useImportAction(groupId, companyId, 'cancel')
  const [confirming, setConfirming] = useState(false)

  if (query.isLoading) return <Skeleton className="h-48 w-full" />
  if (query.isError)
    return <AppError error={query.error} messages={messages} retry={query.refetch} />
  const item: DataImport | undefined = query.data
  if (!item) return null
  const totals = (item.report?.sections ?? []).reduce(
    (sum, section) => ({
      created: sum.created + section.created,
      updated: sum.updated + section.updated,
    }),
    { created: 0, updated: 0 },
  )

  return (
    <section
      className="mb-6 rounded-md border border-border bg-card p-4"
      aria-labelledby="data-import-panel-title"
      data-testid="data-import-panel"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id="data-import-panel-title" className="truncate text-h4">
              {item.original_name}
            </h3>
            <ImportStatusBadge
              status={item.status}
              label={text.statusLabel(item.status)}
              data-testid="data-import-status"
            />
          </div>
          <p className="text-small text-muted-foreground">
            {[item.uploaded_by, item.created_at ? text.formatDate(item.created_at) : null]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          aria-label={t('companies.imports.close')}
        >
          <XIcon className="size-4" aria-hidden="true" />
        </Button>
      </div>

      <Alert
        severity={SEVERITY[item.status] ?? 'info'}
        className="mb-4"
        data-testid="data-import-message"
      >
        <AlertDescription className="flex items-center gap-2">
          {isWorking(item.status) ? <Spinner className="size-4" /> : null}
          {t(`companies.imports.${statusMessage(item)}`, { defaultValue: '' })}
        </AlertDescription>
      </Alert>

      {item.report ? (
        <ImportReport
          report={item.report}
          labels={text.report}
          formatProblemsMore={text.formatProblemsMore}
          formatAmount={text.formatAmount}
        />
      ) : null}

      {item.status === 'ready' ? (
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={cancel.isPending}
            onClick={() => {
              onAct()
              cancel.mutate(item.id, {
                onError: () => toast.error(t('companies.imports.cancelFailed')),
              })
            }}
            data-testid="data-import-cancel"
          >
            {t('companies.imports.cancel')}
          </Button>
          <Button type="button" onClick={() => setConfirming(true)} data-testid="data-import-apply">
            {t('companies.imports.apply')}
          </Button>
        </div>
      ) : null}

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent closeLabel={t('action.cancel')}>
          <DialogHeader>
            <DialogTitle>{t('companies.imports.applyTitle')}</DialogTitle>
            <DialogDescription>
              {t('companies.imports.applyBody', { ...totals, company: companyName })}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <p className="text-small text-muted-foreground">{t('companies.imports.applyNote')}</p>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirming(false)}>
              {t('companies.imports.keepPreview')}
            </Button>
            <Button
              type="button"
              disabled={apply.isPending}
              data-testid="data-import-apply-confirm"
              onClick={() => {
                onAct()
                apply.mutate(item.id, {
                  onSuccess: () => setConfirming(false),
                  onError: () => toast.error(t('companies.imports.applyFailed')),
                })
              }}
            >
              {apply.isPending ? <Spinner className="size-4" /> : null}
              {t('companies.imports.applyConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
